<template>
  <div class="dashboard">
    <router-view />

    <div class="content--main" @scroll.passive="handleScroll">
      <table class="table">
        <thead>
          <tr>
            <th style="width: 8rem;">Date</th>
            <th style="width: 11rem; max-width: 12rem;">Time</th>
            <th style="min-width: 9rem;">Location</th>
            <th style="min-width: 10rem;">Name</th>
            <th style="min-width: 10rem;">Category</th>
            <th style="min-width: 7.5rem">Invoice #</th>
            <th style="min-width: 8.5rem">Amount</th>
            <th style="width: 6.5rem">Status</th>
          </tr>
        </thead>

        <tbody v-if="bookingsToday.data.length">
          <tr :key="item.id" v-for="item in bookingsToday.data" class="item">
            <td class="item__date">
              <time :datetime="item.startAt">
                {{ formatDate(item.startAt) }}
              </time>
            </td>
            <td class="item__time">
              <time>
                {{ formatTime(item.startAt) }}
              </time>
              -
              <time>
                {{ formatTime(item.endAt) }}
              </time>
            </td>
            <td class="item__location truncate">
              <span :title="item.Package.Product.Location.name">
                {{ item.Package.Product.Location.name }}
              </span>
            </td>
            <td class="item__name truncate">
              <template v-if="!item.Order.User">&mdash;</template>
              <span v-else :title="item.Order.User.fullName">
                {{ item.Order.User.fullName }}
              </span>
            </td>
            <td class="item__category truncate">
              <span :title="item.Package.Product.Category.name">
                {{ item.Package.Product.Category.name }}
              </span>
            </td>
            <td class="item__code">
              <router-link
                :to="
                  `${rootRoute}/booking/${item.Order.userId}/invoice/${item.orderId}`
                "
                class="item__code-link"
              >
                {{ item.Order.code }}
              </router-link>
            </td>
            <td class="item__amount">{{ formatCurrency(item.amount) }}</td>
            <td class="item__status">
              <span :class="getStatusProps(item.Order).className">
                {{ getStatusProps(item.Order).label }}
              </span>
            </td>
          </tr>
          <tr v-if="bookingsUpcoming.data.length">
            <td colspan="8" style="padding: 4px 0;"></td>
          </tr>
        </tbody>

        <tbody
          v-if="
            !loading &&
              !bookingsToday.data.length &&
              bookingsUpcoming.data.length
          "
        >
          <tr>
            <td colspan="8">No bookings today.</td>
          </tr>
        </tbody>

        <tbody v-if="bookingsUpcoming.data.length">
          <tr>
            <td colspan="8" style="padding: 4px 0;"></td>
          </tr>
          <tr :key="item.id" v-for="item in bookingsUpcoming.data" class="item">
            <td class="item__date">
              <time :datetime="item.startAt">
                {{ formatDate(item.startAt) }}
              </time>
            </td>
            <td class="item__time">
              <time>
                {{ formatTime(item.startAt) }}
              </time>
              -
              <time>
                {{ formatTime(item.endAt) }}
              </time>
            </td>
            <td class="item__location truncate">
              <span :title="item.Package.Product.Location.name">
                {{ item.Package.Product.Location.name }}
              </span>
            </td>
            <td class="item__name truncate">
              <template v-if="!item.Order.User">&mdash;</template>
              <span v-else :title="item.Order.User.fullName">
                {{ item.Order.User.fullName }}
              </span>
            </td>
            <td class="item__category truncate">
              <span :title="item.Package.Product.Category.name">
                {{ item.Package.Product.Category.name }}
              </span>
            </td>
            <td class="item__code">
              <router-link
                :to="
                  `${rootRoute}/booking/${item.Order.userId}/invoice/${item.orderId}`
                "
                class="item__code-link"
              >
                {{ item.Order.code }}
              </router-link>
            </td>
            <td class="item__amount">{{ formatCurrency(item.amount) }}</td>
            <td class="item__status">
              <span :class="getStatusProps(item.Order).className">
                {{ getStatusProps(item.Order).label }}
              </span>
            </td>
          </tr>
        </tbody>

        <tbody
          v-if="
            !loading &&
              !(bookingsToday.data.length || bookingsUpcoming.data.length)
          "
        >
          <tr>
            <td colspan="8" style="text-align: center;">
              <div class="placeholder">
                <lottie-player
                  autoplay
                  class="placeholder__player"
                  src="/lottiefiles/99012-calendar.json"
                  style="max-width: 366px; height: auto; margin: 0 auto;"
                />
                <p class="placeholder__heading">
                  There are no upcoming bookings...
                </p>
                <template v-if="rootRoute === '/admin'">
                  <p class="placeholder__subheading">
                    Check your products to make sure they are up to date.
                  </p>
                  <div v-if="rootRoute === '/admin'" class="text-center">
                    <ActionButton
                      class="w-auto m-0 d-inline-block"
                      label="Go to Products"
                      :theme="scheme"
                      @customEvent="() => $router.push('/admin/products')"
                    />
                  </div>
                </template>
              </div>
            </td>
          </tr>
        </tbody>

        <tbody v-if="loading">
          <tr>
            <td colspan="8" style="text-align: center;">
              <Spinner :theme="scheme.primary" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { dateUTC } from "@/helpers/date";
import moment from "moment";
import { mixin as clickaway } from "vue-clickaway";
import ActionButton from "@/components/ActionButton";
import Spinner from "@/components/Spinner";
import _ from "lodash";
import "@lottiefiles/lottie-player";

export default {
  mixins: [clickaway],
  components: { ActionButton, Spinner },
  data() {
    return {
      activeLocation: {},
      activeTab: 1,
      currentDate: moment(dateUTC(new Date())).format("MMMM DD, YYYY"),
      currentTab: {},
      flatPickrConfig: {
        dateFormat: "F d Y"
      },
      indicatorStyle: {},
      isDropdownVisible: false,
      loading: true,
      locations: [],
      resources: [],
      bookingsToday: this.makeListData(),
      bookingsUpcoming: this.makeListData(),
      tabs: [],
      isStatusDropdownVisible: false,
      bookingStatus: [
        { id: 0, name: "All bookings", value: "0" },
        { id: 1, name: "Unpaid", value: "1" },
        { id: 2, name: "Paid", value: "2" }
      ],
      activeStatus: {}
    };
  },
  async created() {
    await Promise.all([
      this.fetchBookingsToday(),
      this.fetchBookingsUpcoming()
    ]);
    this.loading = false;
  },
  watch: {
    currentDate() {
      this.handleTabClick({ data: this.currentTab, params: {} });
    },
    primary() {
      this.indicatorStyle.backgroundColor = this.primary;
    }
  },
  async mounted() {
    /**
     * SOCKET: update concierge order dashboard
     */
    await this.$socket.on("notifyPaidOrder", async data => {
      const tenant = window.location.host.split(".")[0];
      if (data.tenant == tenant) {
        await this.handleTabClick({ data: this.currentTab, params: {} });
      }

      Promise.all([this.fetchBookingsToday(), this.fetchBookingsUpcoming()]);
    });
  },
  computed: {
    scheme() {
      return this.$store.state.app.scheme;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    rootRoute() {
      const [, rootRoute] = this.$route.path.split("/");
      return "/" + rootRoute;
    }
  },
  methods: {
    ...mapActions({
      changeActiveTrail: "changeActiveTrail"
    }),
    makeListData() {
      return {
        loading: true,
        data: [],
        links: { first: null, last: null, next: null, prev: null, self: null },
        meta: {
          totalCount: 0,
          totalPages: 1,
          hasPreviousPage: false,
          hasNextPage: false
        },
        page: { number: 1, size: 100 }
      };
    },
    getStatusProps(order) {
      let className = "item__status--unpaid text-danger";
      let label = "Unpaid";

      if (order.cancelledAt) {
        className = "item__status--cancelled text-warning";
        label = "Cancelled";
      }

      if (order.paidAt) {
        className = "item__status--paid text-success";
        label = "Paid";
      }

      return { className, label };
    },
    formatDate(value) {
      if (!value) return null;
      return moment(value).format("MMM DD, YYYY");
    },
    formatTime(value) {
      if (!value) return null;
      return moment(value).format("h:mm A");
    },
    formatCurrency(value) {
      const currency = new Intl.NumberFormat(void 0, {
        style: "currency",
        currency: "PHP",
        currencyDisplay: "code"
      });
      return currency.format(value);
    },
    async fetchCategories() {
      await this.$http
        .get(`/categories/tabs?date=${this.currentDate}`)
        .then(response => {
          const { data } = response.data;
          this.tabs = data;
          if (this.tabs.length > 0) {
            this.handleTabClick({
              data: this.tabs[0],
              params: this.tabs[0].style
            });
            this.$nextTick(() => {
              this.initTabIndicator();
            });
          }
        })
        .catch(() => {
          this.$swal("Error", "Something happened with the server", "error");
        });
    },
    async fetchLocations(page = 1, size = 10) {
      await this.$http
        .get(`/locations?page[number]=${page}&page[size]=${size}`)
        .then(response => {
          const { data: location, meta } = response.data;
          this.locations = _.orderBy(
            [...this.locations, ...location],
            ["id"],
            ["asc"]
          );
          if (meta.hasNextPage) {
            this.fetchLocations(page + 1);
          }
          if (this.locations.length > 0) {
            this.activeLocation = this.locations[0];
          }
        })
        .catch(() => {
          this.$swal("Error", "Something happened with the server", "error");
        });
    },
    initTabIndicator() {
      const tabs = this.$refs.tabs;

      tabs.map((el, i) => {
        let elWidth = el.offsetWidth;
        let leftPosition = el.offsetLeft;
        this.tabs[i].style.width = `${elWidth}px`;
        this.tabs[i].style.left = `${leftPosition}px`;
        this.tabs[i].style.backgroundColor = this.scheme.primary;
      });
      this.indicatorStyle = this.tabs[0].style;
    },
    updateActiveResource(data) {
      this.activeTab = data.id;
      this.$nextTick(() => {
        this.styleliseTabIndicator(data.style);
      });
    },
    styleliseTabIndicator(params) {
      const { left, width } = params;
      this.indicatorStyle = {
        left,
        width,
        backgroundColor: this.scheme.primary
      };
    },
    async handleTabClick({ data }) {
      const date = this.currentDate;
      const id = this.activeLocation ? this.activeLocation.id : "";
      const status = this.activeStatus.value;
      this.currentTab = data;
      this.resources = [];
      this.loading = true;
      if (id) {
        await this.$http
          .get(
            `/categories/${data.id}/orders?date=${date}&locationId=${id}&status=${status}`
          )
          .then(response => {
            const { data: resources } = response.data;
            for (const resource of Object.keys(resources)) {
              this.resources = this.resources.concat({
                name: resource,
                bookings: resources[resource].map(order => ({
                  by: `${order.user.firstName} ${order.user.lastName}`,
                  startDate: order.order.startAt,
                  endDate: order.order.endAt,
                  agenda: "",
                  status: order.order.paidAt
                }))
              });
            }
          })
          .catch(() => {
            this.$swal("Error", "Something happened", "error");
          });
      }
      this.loading = false;
      this.changeActiveTrail([
        { name: "Home", link: "/concierge" },
        { name: "Dashboard", link: "/concierge" },
        { name: data.label }
      ]);
      this.updateActiveResource(data);
    },
    handleFilterSelect(params) {
      const { item } = params;
      this.activeLocation = item;
      let option = {
        data: this.currentTab,
        params: {}
      };
      this.handleTabClick(option);
    },
    handleFilterByStatus(params) {
      const { item } = params;
      this.activeStatus = item;
      let option = {
        data: this.currentTab,
        params: {}
      };
      this.handleTabClick(option);
    },
    handleBodyClickLocation() {
      this.isDropdownVisible = false;
    },
    handleBodyClickBooking() {
      this.isStatusDropdownVisible = false;
    },
    /**
     * Fetch booked products in each order.
     *
     * @param {URLSearchParams} params
     */
    async fetchBookings(params) {
      try {
        const res = await this.$api(
          `/orders/packages${Array.from(params).length ? `?${params}` : ""}`
        );

        return res.data;
      } catch (error) {
        console.error(error);
      }
    },
    async fetchBookingsToday() {
      const currentDate = moment().startOf("day");
      const params = new URLSearchParams();
      params.set("page[number]", 1);
      params.set("page[size]", 99999);
      params.set("search[startAt]", currentDate.toISOString());
      params.set("search[endAt]", currentDate.endOf("day").toISOString());
      params.set("order[startAt]", "asc");
      params.set("order[createdAt]", "asc");

      const data = await this.fetchBookings(params);
      this.bookingsToday.data = data.data;
      this.bookingsToday.links = data.links;
      this.bookingsToday.meta = data.meta;
    },
    async fetchBookingsUpcoming({ page } = { page: { number: 1, size: 100 } }) {
      const nextDate = moment()
        .add(1, "day")
        .startOf("day");
      const params = new URLSearchParams();
      params.set("page[number]", page.number);
      params.set("page[size]", page.size);
      params.set("search[startAt]", nextDate.toISOString());
      params.set("order[startAt]", "asc");
      params.set("order[createdAt]", "asc");

      this.bookingsUpcoming.loading = true;
      const data = await this.fetchBookings(params);
      this.bookingsUpcoming.data = this.bookingsUpcoming.data.concat(data.data);
      this.bookingsUpcoming.links = data.links;
      this.bookingsUpcoming.meta = data.meta;
      this.bookingsUpcoming.loading = false;

      if (data.meta.hasNextPage) {
        this.bookingsUpcoming.page.number += 1;
      }
    },
    handleScroll(e) {
      const el = e.target;
      const offset = Math.max(200, Math.ceil(el.clientHeight / 2));

      if (this.bookingsUpcoming.loading) return;
      if (!this.bookingsUpcoming.meta.hasNextPage) return;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - offset) return;

      this.fetchBookingsUpcoming({ page: this.bookingsUpcoming.page });
    }
  }
};
</script>

<style lang="scss" scoped>
thead {
  color: $gray-900;
  font-family: $headings-font-family;
}
thead,
td {
  font-size: $font-size-sm;
}
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.scroll-area {
  margin: auto;
  position: relative;
  height: auto;
}
.placeholder {
  margin: clamp(2rem, 10%, 4.5rem) auto;

  &__heading {
    font-size: $font-size-lg;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  &__subheading {
    font-size: $font-size-base;
    margin-bottom: 1rem;
  }
}
.dashboard {
  padding: 0 30px;
  .length-0 {
    color: $gray-900;
    font-family: "Gotham Book";
    font-size: $font-size-sm;
    text-align: left;
  }
}
.content--main {
  background-color: #f8f8f8;
  /* height of viewport minus the sum of nav.header and .content--header */
  height: calc(100vh - var(--navbar-height, 152px));
  overflow: hidden auto;
  padding: 0 0.75rem 0.75rem;
}
.table {
  font-family: "Gotham Book", sans-serif;
  table-layout: fixed;
  tbody + tbody {
    border-top: 1px solid #ddd;
  }
  td {
    border-top: 0;
    padding: 0.75rem;
  }
  th {
    background-color: #f8f8f8;
    position: sticky;
    top: 0;
    padding: 1.5rem 0.75rem 0.75rem;
    text-transform: inherit;
  }
}
.item {
  display: table-row;
  font-size: $font-size-sm;
  font-weight: 400;
  height: auto;
  line-height: 1.25;
  text-align: initial;
  width: auto;
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}
.item__code {
  &-link {
    color: var(--primary);
    font-family: "Gotham Book", sans-serif;
    outline: 0.125rem solid transparent;
    outline-offset: 0.125rem;
    &:focus-visible {
      outline-color: var(--primary);
    }
  }
}
</style>
